// Components/Pages/Partners.jsx
import { useState, useEffect } from "react";
import { FaHandshake, FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";

// Import partner logos - Correct path with "patners" folder
import amkeni from "../../assets/patners/Amkeni.jpeg";
import amplify from "../../assets/patners/Amplify.jpeg";
import global from "../../assets/patners/global.png";
import green from "../../assets/patners/Green.jpeg";
import gym from "../../assets/patners/GYM.png";
import nl from "../../assets/patners/NL.jpeg";
import sdi from "../../assets/patners/SDI.png";
import shofco from "../../assets/patners/shofco.png";
import undp from "../../assets/patners/UNDP.jpeg";

const API_BASE = 'https://api.rigokenya.org';

function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Partner data with 9 partner logos
  const partnerData = [
    {
      id: 1,
      name: "Amkeni Wakenya",
      description: "Partners in strengthening community resilience and empowering local communities.",
      logo: amkeni,
      category: "NGO",
      website: "#"
    },
    {
      id: 2,
      name: "Amplify Change",
      description: "Collaborating to amplify voices and create meaningful change in communities.",
      logo: amplify,
      category: "NGO",
      website: "https://amplifychange.org/about/"
    },
    {
      id: 3,
      name: "Global Youth Mobilization",
      description: "Empowering youth through global partnerships and youth-led initiatives.",
      logo: gym,
      category: "International",
      website: "https://globalyouthmobilization.org/"
    },
    {
      id: 4,
      name: "Kakamega County",
      description: "Working together on environmental conservation and sustainable practices.",
      logo: green,
      category: "Environment",
      website: "https://kakamega.go.ke/"
    },
    {
      id: 5,
      name: "+1 Global Fund",
      description: "International partnership for global development and community transformation.",
      logo: global,
      category: "International",
      website: "#"
    },
    {
      id: 6,
      name: "Netherlands Embassy",
      description: "Supporting gender equality, women's empowerment and sustainable development.",
      logo: nl,
      category: "Government",
      website: "https://www.netherlandsandyou.nl/web/kenya/home"
    },
    {
      id: 7,
      name: "SDI Kenya",
      description: "Partnering on urban development and community-led initiatives.",
      logo: sdi,
      category: "International",
      website: "https://www.sdikenya.org/"
    },
    {
      id: 8,
      name: "SHOFCO",
      description: "Collaborating on urban and rural development programs for community transformation.",
      logo: shofco,
      category: "NGO",
      website: "https://www.shofco.org/"
    },
    {
      id: 9,
      name: "UNDP",
      description: "Collaborating on sustainable development initiatives and community empowerment.",
      logo: undp,
      category: "International",
      website: "https://www.undp.org/kenya"
    }
  ];

  useEffect(() => {
    // Simulate API fetch
    const fetchPartners = async () => {
      try {
        setLoading(true);
        // Try to fetch from API if available
        const response = await fetch(`${API_BASE}/api/partners`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setPartners(data);
            setLoading(false);
            return;
          }
        }
        // Fallback to sample data
        setPartners(partnerData);
      } catch (error) {
        console.error('Error fetching partners:', error);
        setPartners(partnerData);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/api/placeholder/200/120';
    // If it's an imported image (starts with /static/), return as is
    if (typeof imagePath === 'string' && imagePath.startsWith('/static/')) {
      return imagePath;
    }
    if (typeof imagePath === 'string' && imagePath.startsWith('/uploads/')) {
      return `${API_BASE}${imagePath}`;
    }
    if (typeof imagePath === 'string' && imagePath.startsWith('http')) {
      return imagePath;
    }
    // For imported images, they are already processed by webpack
    return imagePath;
  };

  // Get unique categories
  const categories = ["All", ...new Set(partners.map(p => p.category).filter(Boolean))];
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredPartners = selectedCategory === "All" 
    ? partners 
    : partners.filter(p => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primaryGold mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading partners...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-primaryGold/10 p-3 rounded-full mb-4">
            <FaHandshake className="text-4xl text-primaryGold" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-primaryBlue">
            Our <span className="text-primaryGold">Partners</span>
          </h1>
          <p className="text-gray-600 mt-2 text-lg max-w-2xl mx-auto">
            Collaborating with organizations and institutions to create lasting impact in communities
          </p>
          <div className="w-24 h-1 bg-primaryGold mx-auto mt-4"></div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedCategory === category
                  ? "bg-primaryGold text-primaryBlue shadow-md"
                  : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Partners Grid */}
        {filteredPartners.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPartners.map((partner) => (
              <div
                key={partner.id}
                className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:-translate-y-2"
              >
                <div className="p-8 flex items-center justify-center h-48 bg-gray-50 border-b border-gray-100">
                  <img
                    src={getImageUrl(partner.logo)}
                    alt={partner.name}
                    className="max-h-32 w-auto object-contain group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/200/120';
                    }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-primaryBlue mb-2">{partner.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{partner.description}</p>
                  <div className="mt-4 flex items-center justify-between">
                    {partner.category && (
                      <span className="bg-primaryGold/10 text-primaryGold text-xs px-2 py-1 rounded-full">
                        {partner.category}
                      </span>
                    )}
                    {partner.website && partner.website !== '#' && (
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primaryBlue hover:text-primaryGold transition-colors duration-300 text-sm font-medium flex items-center gap-1"
                      >
                        Visit <FaArrowRight className="text-xs" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <FaHandshake className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-gray-700">No partners found</h3>
            <p className="text-gray-500 mt-2">Check back later for our partners</p>
          </div>
        )}

        {/* Partner Counter */}
        {filteredPartners.length > 0 && (
          <div className="text-center mt-8 text-gray-500 text-sm">
            {filteredPartners.length} {filteredPartners.length === 1 ? 'partner' : 'partners'}
            {selectedCategory !== "All" && ` in "${selectedCategory}"`}
          </div>
        )}

        {/* Become a Partner CTA */}
        <div className="mt-16 bg-primaryBlue text-white rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Interested in Partnering with Us?
          </h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Join us in transforming communities and creating lasting impact. We welcome partnerships that align with our mission.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-primaryGold text-primaryBlue px-8 py-3 rounded-full font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Get in Touch
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Partners;